import { FlexCol } from "@design-system/Layout/Flex";
import { useMemo, type FC } from "react";

import { AiSuggestion } from "@app/shared/components/ai/AiSuggestion";

import { type SupportedLocales } from "@app/shared/utils/locales";
import {
    AnswerStatus,
    type GetQuestionsQuery,
} from "@generated/client/graphql";
import { useQuestionsTabTranslations } from "../i18n";
import { useAiSuggestionConfirm } from "./useAiSuggestionConfirm";

export const AnswerAiSuggestionBlock: FC<{
    question: GetQuestionsQuery["questions"][number];
    locale: SupportedLocales;
    withInsert?: boolean;
    withCopy?: boolean;
}> = ({ question, locale, withInsert, withCopy }) => {
    const { t, keys } = useQuestionsTabTranslations();

    const aiSuggestion = question?.answer?.aiSuggestion;

    const { aiSuggestionSuccess, isSuggestError } = useMemo(() => {
        const success = aiSuggestion?.success;
        const isSuggestError = aiSuggestion?.success === false;

        return {
            aiSuggestionSuccess: success ?? false,
            isSuggestError,
        };
    }, [question?.answer?.aiSuggestion]);

    const { handleInsertAnswer, handleInsertComment, answerText, comment } =
        useAiSuggestionConfirm({
            question,
            locale,
        });

    return !isSuggestError || !!comment ? (
        <>
            <AiSuggestion.Separator />
            <FlexCol gap="4">
                {!isSuggestError && (
                    <AiSuggestion.Block
                        title={t(keys.ai_suggestion_answer)}
                        content={answerText ?? ""}
                        withCopy={withCopy ?? aiSuggestionSuccess}
                        withInsert={
                            withInsert ??
                            (aiSuggestionSuccess &&
                                question?.answer?.status !==
                                    AnswerStatus.Validated)
                        }
                        handleInsertLinkClick={handleInsertAnswer}
                    />
                )}
                {!!comment && (
                    <AiSuggestion.Block
                        title={t(keys.ai_suggestion_comment)}
                        content={comment ?? ""}
                        withCopy={withCopy ?? aiSuggestionSuccess}
                        withInsert={
                            withInsert ??
                            (aiSuggestionSuccess &&
                                question?.answer?.status !==
                                    AnswerStatus.Validated)
                        }
                        handleInsertLinkClick={handleInsertComment}
                    />
                )}
            </FlexCol>
        </>
    ) : null;
};
